import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog } from 'components/modals/common/ConfirmDialog';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';
import { SyutSelect } from 'components/ui/SyutSelect';
import notificationService from 'services/notificationService';
import { formatBackendTimestamps } from 'utils/errorHandling';
import { addonService } from 'api/services/addonService';
import { propertyService } from 'api/services/propertyService';
import type { Property } from 'api/types';
import type { Addon, CreateAddonRequest, UpdateAddonRequest } from 'api/types/addon.types';
import type { SelectOption } from 'components/ui/SyutSelect';

type AddonModalMode = 'create' | 'edit' | 'copy' | 'view';

interface AddonModalProps {
  isOpen: boolean;
  mode: AddonModalMode;
  record?: Addon | null;
  onSave: () => void;
  onClose: () => void;
  tabId?: string;
  moduleName?: string;
  createInstanceId?: string;
  copyInstanceId?: string;
  minimizeRef?: React.MutableRefObject<(() => void) | null>;
  onRestoreModal?: (mode: AddonModalMode, instanceId?: string, recordCode?: string) => void;
  /** Cached property lookup for inactive-FK label resolution. */
  propertyLookup?: Record<string, Property>;
}

interface AddonFormData {
  addonCode: string;
  addonName: string;
  description: string;
  serviceType: string;
  mealType: string;
  propertyId: string;
  isActive: boolean;
}

const INITIAL_FORM_DATA: AddonFormData = {
  addonCode: '',
  addonName: '',
  description: '',
  serviceType: 'SERVICE',
  mealType: '',
  propertyId: '',
  isActive: true,
};

const AddonModal: React.FC<AddonModalProps> = ({
  isOpen,
  mode,
  record,
  onSave,
  onClose,
  tabId,
  moduleName,
  createInstanceId,
  copyInstanceId,
  minimizeRef,
  onRestoreModal,
  propertyLookup,
}) => {
  const { minimize } = useModalMinimize();

  // --- Form state (Pattern B — modal owns formData) ---
  const [formData, setFormData] = useState<AddonFormData>(INITIAL_FORM_DATA);
  const formDataRef = useRef(formData);
  formDataRef.current = formData;
  const modeRef = useRef(mode);
  modeRef.current = mode;
  const recordRef = useRef(record);
  recordRef.current = record;
  const createInstanceIdRef = useRef(createInstanceId);
  createInstanceIdRef.current = createInstanceId;
  const copyInstanceIdRef = useRef(copyInstanceId);
  copyInstanceIdRef.current = copyInstanceId;
  const isRestoreRef = useRef(false);
  const initialSnapshotRef = useRef('');
  const effectiveRecordRef = useRef<Addon | null | undefined>(record);

  const isReadOnly = mode === 'view';

  // --- Field-level validation ---
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const getFieldError = useCallback((field: string) => fieldErrors[field], [fieldErrors]);
  const clearFieldError = useCallback((field: string) => {
    setFieldErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, []);

  // --- Confirm dialog + save state ---
  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [concurrencyConflict, setConcurrencyConflict] = useState<{ open: boolean; message: string }>({
    open: false,
    message: '',
  });
  const [isRefreshing, setIsRefreshing] = useState(false);

  // --- Async property options (always fresh from DB, with inactive-FK label seed) ---
  const loadPropertyOptions = useCallback(async (inputValue: string): Promise<SelectOption[]> => {
    const res = await propertyService.getAll({
      searchTerm: inputValue || undefined,
      pageSize: 50,
      includeInactive: false,
    });
    return (res.data || []).map((p) => ({
      value: p.id,
      label: `${p.propertyCode} - ${p.propertyName}`,
    }));
  }, []);

  // Seed an inactive property option from the parent's lookup so the form can
  // resolve the label for a property that's now inactive (would otherwise be
  // missing from loadOptions output).
  const initialPropertyOption = useMemo<SelectOption | null>(() => {
    if (!record?.propertyId || !propertyLookup) return null;
    const prop = propertyLookup[record.propertyId];
    if (!prop) return null;
    const isInactive = prop.isActive === false;
    return {
      value: prop.id,
      label: `${prop.propertyCode} - ${prop.propertyName}${isInactive ? ' (Inactive)' : ''}`,
    };
  }, [record, propertyLookup]);

  // --- Populate form on open ---
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!isOpen) return;
    effectiveRecordRef.current = record;
    if (isRestoreRef.current) {
      isRestoreRef.current = false;
      setFieldErrors({});
      return;
    }
    const rec = recordRef.current;
    let data: AddonFormData;
    if ((mode === 'edit' || mode === 'copy' || mode === 'view') && rec) {
      data = {
        addonCode: mode === 'copy' ? '' : rec.addonCode || '',
        addonName: rec.addonName || '',
        description: rec.description || '',
        serviceType: rec.serviceType || 'SERVICE',
        mealType: rec.mealType || '',
        propertyId: rec.propertyId || '',
        isActive: rec.isActive,
      };
    } else {
      data = { ...INITIAL_FORM_DATA };
    }
    setFormData(data);
    initialSnapshotRef.current = JSON.stringify(data);
    setFieldErrors({});
  }, [isOpen]);

  // --- Unsaved changes tracking ---
  const isDirty = useMemo(() => {
    if (!initialSnapshotRef.current) return false;
    return JSON.stringify(formData) !== initialSnapshotRef.current;
  }, [formData]);

  const handleClose = useCallback(() => {
    if (!isDirty || isReadOnly) {
      onClose();
    } else {
      setDiscardConfirmOpen(true);
    }
  }, [isDirty, isReadOnly, onClose]);

  // --- Minimize handler ---
  const handleMinimize = useCallback(() => {
    if (!tabId || !moduleName) return;
    const currentFormData = formDataRef.current;
    const currentMode = modeRef.current;
    const currentCreateInstanceId = createInstanceIdRef.current;
    const currentCopyInstanceId = copyInstanceIdRef.current;
    const currentRecord = recordRef.current;

    let modalId: string;
    if (currentMode === 'create') {
      modalId = `${tabId}-form-modal-create-${currentCreateInstanceId || 'default'}`;
    } else if (currentMode === 'copy') {
      modalId = `${tabId}-form-modal-copy-${currentCopyInstanceId || 'default'}`;
    } else if (currentMode === 'view') {
      modalId = `${tabId}-view-modal-${currentRecord?.id || 'new'}`;
    } else {
      modalId = `${tabId}-form-modal-edit-${currentRecord?.id || 'new'}`;
    }

    let title: string;
    if (currentMode === 'create') {
      const code = currentFormData.addonCode?.trim();
      const name = currentFormData.addonName?.trim();
      const idx = currentCreateInstanceId || '1';
      title = code ? `Add - ${code} (${idx})` : name ? `Add - ${name} (${idx})` : `Add Add-on (${idx})`;
    } else if (currentMode === 'copy') {
      const code = currentFormData.addonCode?.trim();
      const name = currentFormData.addonName?.trim();
      const idx = currentCopyInstanceId || '1';
      title = code ? `Copy - ${code} (${idx})` : name ? `Copy - ${name} (${idx})` : `Copy (${idx})`;
    } else if (currentMode === 'view') {
      title = `View - ${currentFormData.addonCode || 'Add-on'}`;
    } else {
      title = `Edit - ${currentFormData.addonCode || 'Add-on'}`;
    }

    const savedFormData = { ...currentFormData };
    const savedSnapshot = initialSnapshotRef.current;
    const currentIsDirty = savedSnapshot ? JSON.stringify(currentFormData) !== savedSnapshot : false;

    minimize({
      id: modalId,
      title,
      moduleName,
      tabId,
      isDirty: currentIsDirty,
      onRestore: () => {
        isRestoreRef.current = true;
        setFormData(savedFormData);
        initialSnapshotRef.current = savedSnapshot;
        if (onRestoreModal)
          onRestoreModal(
            currentMode,
            currentMode === 'create'
              ? currentCreateInstanceId
              : currentMode === 'copy'
              ? currentCopyInstanceId
              : undefined,
            currentMode !== 'create' ? currentRecord?.addonCode : undefined,
          );
      },
    });
    onClose();
  }, [tabId, moduleName, minimize, onClose, onRestoreModal]);

  // Expose handleMinimize to parent for auto-minimize on new copy
  if (minimizeRef) minimizeRef.current = handleMinimize;

  // --- Code/name validation on blur ---
  const handleCodeBlur = useCallback(async () => {
    const code = formData.addonCode.trim();
    if (!code || isReadOnly) return;
    try {
      const propertyId = formData.propertyId || undefined;
      const excludeId = mode === 'edit' && record ? record.id : undefined;
      const response = await addonService.validateCode(code, propertyId, excludeId);
      if (!response.data) {
        setFieldErrors((prev) => ({ ...prev, addonCode: 'This add-on code already exists' }));
      }
    } catch {
      /* ignore */
    }
  }, [formData.addonCode, formData.propertyId, mode, record, isReadOnly]);

  const handleNameBlur = useCallback(async () => {
    const name = formData.addonName.trim();
    if (!name || isReadOnly) return;
    try {
      const propertyId = formData.propertyId || undefined;
      const excludeId = mode === 'edit' && record ? record.id : undefined;
      const response = await addonService.validateName(name, propertyId, excludeId);
      if (!response.data) {
        setFieldErrors((prev) => ({ ...prev, addonName: 'This add-on name already exists' }));
      }
    } catch {
      /* ignore */
    }
  }, [formData.addonName, formData.propertyId, mode, record, isReadOnly]);

  // --- Validate + show save confirmation ---
  const handleSave = useCallback(async () => {
    if (isReadOnly) return;
    const errors: Record<string, string> = {};

    if (!formData.propertyId) {
      errors.propertyId = 'Property is required';
    }
    if (!formData.addonCode.trim()) {
      errors.addonCode = 'Add-on Code is required';
    } else if (formData.addonCode.length > 30) {
      errors.addonCode = 'Add-on Code cannot exceed 30 characters';
    }
    if (!formData.addonName.trim()) {
      errors.addonName = 'Add-on Name is required';
    } else if (formData.addonName.length > 100) {
      errors.addonName = 'Add-on Name cannot exceed 100 characters';
    }
    if (formData.description && formData.description.length > 500) {
      errors.description = 'Description cannot exceed 500 characters';
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      requestAnimationFrame(() => {
        const el = document.querySelector<HTMLElement>(`[data-field="${Object.keys(errors)[0]}"]`);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          el.querySelector<HTMLElement>('input')?.focus();
        }
      });
      return;
    }

    // Async uniqueness validation for create/copy
    if (mode !== 'edit') {
      try {
        const propertyId = formData.propertyId || undefined;
        const codeResp = await addonService.validateCode(formData.addonCode.trim(), propertyId);
        if (!codeResp.data) {
          setFieldErrors({ addonCode: 'This add-on code already exists' });
          return;
        }
      } catch {
        /* ignore */
      }
    }
    try {
      const propertyId = formData.propertyId || undefined;
      const excludeId = mode === 'edit' && record ? record.id : undefined;
      const nameResp = await addonService.validateName(formData.addonName.trim(), propertyId, excludeId);
      if (!nameResp.data) {
        setFieldErrors({ addonName: 'This add-on name already exists' });
        return;
      }
    } catch {
      /* ignore */
    }

    setFieldErrors({});
    setSaveConfirmOpen(true);
  }, [formData, mode, record, isReadOnly]);

  // --- Confirmed save (3-stage flow) ---
  const handleConfirmSave = useCallback(async () => {
    setSaveConfirmOpen(false);
    setIsSaving(true);
    try {
      const payload: CreateAddonRequest | UpdateAddonRequest = {
        propertyId: formData.propertyId || undefined,
        addonCode: formData.addonCode.trim(),
        addonName: formData.addonName.trim(),
        description: formData.description || undefined,
        serviceType: formData.serviceType || 'SERVICE',
        // Only a MEAL add-on may carry a meal type (chk_addon_meal_type_requires_meal).
        mealType: formData.serviceType === 'MEAL' && formData.mealType
          ? formData.mealType
          : null,
        isActive: formData.isActive,
      };

      if (mode === 'edit') {
        const eff = effectiveRecordRef.current!;
        await addonService.update(eff.id, { ...payload, rowVersion: eff.rowVersion } as UpdateAddonRequest);
      } else {
        await addonService.create(payload as CreateAddonRequest);
      }

      setIsSaving(false);
      notificationService.toast.success(
        `"${formData.addonName}" ${mode === 'edit' ? 'updated' : 'created'} successfully.`,
      );
      onSave();
    } catch (err: any) {
      setIsSaving(false);
      const status = err?.response?.status;
      const backendMessage =
        err?.response?.data?.message ||
        err?.response?.data?.errors?.[0] ||
        err?.message;
      if (status === 409 && backendMessage) {
        setConcurrencyConflict({ open: true, message: formatBackendTimestamps(backendMessage) });
      } else {
        notificationService.toast.error(backendMessage || 'An error occurred. Please try again.');
      }
    }
  }, [formData, mode, onSave]);

  const handleRefreshFromConflict = useCallback(async () => {
    const eff = effectiveRecordRef.current;
    if (!eff) {
      setConcurrencyConflict({ open: false, message: '' });
      return;
    }
    setIsRefreshing(true);
    try {
      const response = await addonService.getById(eff.id);
      const rec = response.data;
      if (rec) {
        const data: AddonFormData = {
          addonCode: rec.addonCode || '',
          addonName: rec.addonName || '',
          description: rec.description || '',
          serviceType: rec.serviceType || 'SERVICE',
          mealType: rec.mealType || '',
          propertyId: rec.propertyId || '',
          isActive: rec.isActive,
        };
        setFormData(data);
        initialSnapshotRef.current = JSON.stringify(data);
        effectiveRecordRef.current = rec;
        setFieldErrors({});
        notificationService.toast.success('Loaded the latest version. Please review and save.');
      }
      setConcurrencyConflict({ open: false, message: '' });
    } catch (err: any) {
      const backendMessage =
        err?.response?.data?.message ||
        err?.response?.data?.errors?.[0] ||
        err?.message ||
        'Failed to load the latest version.';
      notificationService.toast.error(backendMessage);
    } finally {
      setIsRefreshing(false);
    }
  }, []);

  if (!isOpen) return null;

  const modalTitle =
    mode === 'create'
      ? 'Add Add-on'
      : mode === 'copy'
      ? 'Copy Add-on'
      : mode === 'view'
      ? 'View Add-on'
      : 'Edit Add-on';

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-md" style={{ minHeight: 'auto' }}>
        {/* Loading overlay */}
        {(isSaving || isRefreshing) && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              background: 'var(--modal-loading-overlay)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10,
              borderRadius: 'inherit',
              gap: '0.75rem',
            }}
          >
            <div
              style={{
                width: '2rem',
                height: '2rem',
                border: '3px solid var(--border-color)',
                borderTopColor: '#2563eb',
                borderRadius: '50%',
                animation: 'spin 0.8s linear infinite',
              }}
            />
            <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 500 }}>
              {isRefreshing ? 'Refreshing...' : mode === 'edit' ? 'Saving...' : 'Creating...'}
            </span>
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          </div>
        )}

        <div className="modal-header">
          <h2 className="modal-title">{modalTitle}</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
            {tabId && (
              <button
                className="modal-header-btn"
                onClick={handleMinimize}
                title="Minimize to bar"
                disabled={isSaving || isRefreshing}
              >
                <IconSystem.chevronDown size={16} />
              </button>
            )}
            <button className="modal-close" onClick={handleClose} disabled={isSaving || isRefreshing}>
              <IconSystem.x size={20} />
            </button>
          </div>
        </div>

        <div className="modal-content" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          {/* Property */}
          <div className="form-group" data-field="propertyId">
            <SyutSelect
              label={<>Property <span className="text-danger">*</span></>}
              isAsync
              loadOptions={loadPropertyOptions}
              defaultOptions
              cacheOptions={false}
              seedOptions={initialPropertyOption ? [initialPropertyOption] : undefined}
              value={formData.propertyId}
              onChange={(val) => {
                clearFieldError('propertyId');
                setFormData((prev) => ({ ...prev, propertyId: (val as string) || '' }));
              }}
              placeholder="Select property..."
              isClearable
              isDisabled={isReadOnly}
              size="md"
              error={getFieldError('propertyId')}
            />
          </div>

          {/* Code & Name */}
          <div className="form-grid-2">
            <div className="form-group" data-field="addonCode">
              <label className="form-label">
                Code <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                className={`form-input${getFieldError('addonCode') ? ' form-field-error' : ''}`}
                value={formData.addonCode}
                onChange={(e) => {
                  clearFieldError('addonCode');
                  setFormData((prev) => ({ ...prev, addonCode: e.target.value }));
                }}
                onBlur={handleCodeBlur}
                placeholder="e.g., SPA-AROMA"
                maxLength={30}
                readOnly={mode === 'edit' || isReadOnly}
                style={mode === 'edit' || isReadOnly ? { background: 'var(--bg-secondary)' } : undefined}
                autoFocus={mode === 'create' || mode === 'copy'}
              />
              {getFieldError('addonCode') && (
                <div className="form-error-message">{getFieldError('addonCode')}</div>
              )}
            </div>

            <div className="form-group" data-field="addonName">
              <label className="form-label">
                Name <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                className={`form-input${getFieldError('addonName') ? ' form-field-error' : ''}`}
                value={formData.addonName}
                onChange={(e) => {
                  clearFieldError('addonName');
                  setFormData((prev) => ({ ...prev, addonName: e.target.value }));
                }}
                onBlur={handleNameBlur}
                placeholder="e.g., Aromatherapy Spa"
                maxLength={100}
                readOnly={isReadOnly}
                style={isReadOnly ? { background: 'var(--bg-secondary)' } : undefined}
              />
              {getFieldError('addonName') && (
                <div className="form-error-message">{getFieldError('addonName')}</div>
              )}
            </div>
          </div>

          {/* Description */}
          <div className="form-group" data-field="description">
            <label className="form-label">Description</label>
            <textarea
              className={`form-textarea${getFieldError('description') ? ' form-field-error' : ''}`}
              value={formData.description}
              onChange={(e) => {
                clearFieldError('description');
                setFormData((prev) => ({ ...prev, description: e.target.value }));
              }}
              placeholder="Optional description"
              maxLength={500}
              rows={3}
              readOnly={isReadOnly}
              style={isReadOnly ? { background: 'var(--bg-secondary)' } : undefined}
            />
            {getFieldError('description') && (
              <div className="form-error-message">{getFieldError('description')}</div>
            )}
          </div>

          {/* Operational routing — decides which worklist the item appears on. */}
          <div className="form-grid-2">
            <div className="form-group" data-field="serviceType">
              <SyutSelect
                label="Service Type"
                options={[
                  { value: 'SERVICE', label: 'Service (banquet worklist)' },
                  { value: 'MEAL', label: 'Meal (kitchen food plan)' },
                  { value: 'VENUE', label: 'Venue (banquet worklist)' },
                ]}
                value={formData.serviceType}
                onChange={(val) => setFormData((prev) => ({
                  ...prev,
                  serviceType: (val as string) || 'SERVICE',
                  // Dropping out of MEAL must clear the meal type, or the row
                  // violates the DB check that ties one to the other.
                  mealType: (val as string) === 'MEAL' ? prev.mealType : '',
                }))}
                isClearable={false}
                isDisabled={isReadOnly}
                size="sm"
              />
            </div>

            {formData.serviceType === 'MEAL' && (
              <div className="form-group" data-field="mealType">
                <SyutSelect
                  label="Counts Towards"
                  options={[
                    { value: '', label: 'Own entry (high tea, snacks…)' },
                    { value: 'BREAKFAST', label: 'Breakfast covers' },
                    { value: 'LUNCH', label: 'Lunch covers' },
                    { value: 'DINNER', label: 'Dinner covers' },
                  ]}
                  value={formData.mealType}
                  onChange={(val) => setFormData((prev) => ({ ...prev, mealType: (val as string) || '' }))}
                  isClearable={false}
                  isDisabled={isReadOnly}
                  size="sm"
                />
                <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>
                  {formData.mealType
                    ? 'Added to that meal’s covers, and listed by name for the group.'
                    : 'Listed as its own entry under the day’s covers, counted nowhere else.'}
                </div>
              </div>
            )}
          </div>

          {/* Status */}
          <div className="form-group" data-field="isActive">
            <SyutSelect
              label="Status"
              options={[
                { value: 'true', label: 'Active' },
                { value: 'false', label: 'Inactive' },
              ]}
              value={String(formData.isActive)}
              onChange={(val) => setFormData((prev) => ({ ...prev, isActive: val === 'true' }))}
              isClearable={false}
              isDisabled={isReadOnly}
              size="md"
            />
          </div>
        </div>

        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button
            className="btn btn-secondary"
            onClick={handleClose}
            disabled={isSaving || isRefreshing}
          >
            {isReadOnly ? 'Close' : 'Cancel'}
          </button>
          {!isReadOnly && (
            <button
              className="btn btn-primary"
              onClick={handleSave}
              disabled={
                isSaving ||
                isRefreshing ||
                (mode === 'edit' && !isDirty)
              }
              title={mode === 'edit' && !isDirty ? 'No changes to save' : undefined}
            >
              Save
            </button>
          )}
        </div>
      </div>

      <ConfirmDialog
        open={saveConfirmOpen}
        title="Confirm Save"
        message={`Are you sure you want to ${mode === 'edit' ? 'update' : 'create'} this add-on?`}
        confirmLabel="Confirm"
        cancelLabel="Cancel"
        variant="default"
        onConfirm={handleConfirmSave}
        onCancel={() => setSaveConfirmOpen(false)}
      />

      <ConfirmDialog
        open={discardConfirmOpen}
        title="Discard Changes?"
        message="You have unsaved changes. Do you want to discard them?"
        confirmLabel="Discard"
        cancelLabel="Keep Editing"
        variant="danger"
        onConfirm={() => {
          setDiscardConfirmOpen(false);
          onClose();
        }}
        onCancel={() => setDiscardConfirmOpen(false)}
      />

      <ConfirmDialog
        open={concurrencyConflict.open}
        title="Record Was Updated"
        message={concurrencyConflict.message || 'This record has been updated by another user. Reload latest version?'}
        confirmLabel="Reload"
        cancelLabel="Cancel"
        variant="default"
        onConfirm={handleRefreshFromConflict}
        onCancel={() => setConcurrencyConflict({ open: false, message: '' })}
      />
    </>
  );
};

export default AddonModal;
